import { fetchApiJson } from '@vend/utilities'
import * as Ably from 'ably'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { useWebRegisterStatusManager } from '../../ng-compat/hooks'
import { getFeatureFromCache } from '../../util/feature-cache'

const FEATURE_TERMINAL_STATUS_CONNECTION_LOGGING_ENABLED =
  'lspay_terminal_status_connection_logging_enabled'

export type RealtimeContextState = {
  client: Ably.Realtime | null
  serialNumber: string | null
}

export const RealtimeContext = createContext<RealtimeContextState>({
  client: null,
  serialNumber: null,
})

interface RealtimeProviderProps {
  outletId: string
  registerId: string
  children: ReactNode
}

const getTokenUrl = (outletId: string, registerId: string) =>
  `/api/2.0/outlets/${outletId}/lspay/token/${registerId}?pairingData=true`

const getAuthUrl = (registerId: string) =>
  `/api/1.0/cfd/lspay/auth/${registerId}`

export function RealtimeProvider({
  outletId,
  registerId,
  children,
}: RealtimeProviderProps) {
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const isOnline = webRegisterStatusManager?.isOnline() ?? false

  const [client, setClient] = useState<Ably.Realtime | null>(null)
  const [serialNumber, setSerialNumber] = useState<string | null>(null)

  useEffect(() => {
    if (!isOnline || !registerId || !outletId) {
      setClient(null)
      setSerialNumber(null)
      return
    }

    const terminalStatusConnectionLoggingEnabled = getFeatureFromCache(
      FEATURE_TERMINAL_STATUS_CONNECTION_LOGGING_ENABLED
    )

    let ablyClient: Ably.Realtime | null = null
    let cancelled = false

    const setupAblyClient = async () => {
      const terminalInfo = await fetchApiJson(getTokenUrl(outletId, registerId))

      if (cancelled) {
        return
      }

      setSerialNumber(terminalInfo.serial_number)

      const newClient = new Ably.Realtime({
        authUrl: getAuthUrl(registerId),
        closeOnUnload: true,
      })

      if (cancelled) {
        newClient.close()
        return
      }

      ablyClient = newClient
      setClient(ablyClient)

      if (terminalStatusConnectionLoggingEnabled) {
        datadogLogs.logger.info('Provisioned Realtime Client', {
          provider: 'ably',
          client: ablyClient.clientId,
          outletId,
          registerId,
          terminalSerialNumber: terminalInfo.serial_number,
          connectionId: ablyClient.connection.id,
        })
      }
    }

    setupAblyClient()

    return () => {
      cancelled = true

      if (terminalStatusConnectionLoggingEnabled && ablyClient) {
        const connectionId = ablyClient.connection.id
        const clientId = ablyClient.clientId

        datadogLogs.logger.info('Closed Realtime Client', {
          provider: 'ably',
          client: clientId,
          outletId,
          registerId,
          connectionId,
        })
      }

      ablyClient?.close()
    }
  }, [isOnline, outletId, registerId])

  return (
    <RealtimeContext.Provider value={{ client, serialNumber }}>
      {children}
    </RealtimeContext.Provider>
  )
}

export function useRealtimeProvider(): RealtimeContextState {
  return useContext(RealtimeContext)
}
